import { useState, useEffect } from 'react';
import { useAuth } from '@/lib/auth-context';
import { useNavigate } from 'react-router-dom';
import * as db from '@/lib/data-service';
import { Button } from '@/components/ui/button';
import { LogOut, Camera, Users, Calendar, Flag, Check } from 'lucide-react';
import { calcAge } from '@/lib/constants';

export default function AwaitingVouch() {
  const { profile, logout } = useAuth();
  const navigate = useNavigate();
  const [vouchCount, setVouchCount] = useState(0);

  useEffect(() => {
    if (profile?.id) db.getVouchCount(profile.id).then(setVouchCount);
  }, [profile?.id]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (!profile) {
    return (
      <div className="h-[100dvh] flex items-center justify-center bg-[#FAF8F5]">
        <p className="text-sm text-[#A8A5A0]">Loading...</p>
      </div>
    );
  }

  const coverPhoto = db.getPhotos(profile.id)?.[0];
  const age = calcAge(profile.dob);
  const months = getMonthsOnPlatform(profile.created_at);

  return (
    <div className="h-[100dvh] flex flex-col bg-[#FAF8F5]">
      {/* Header */}
      <div className="px-4 pt-5 pb-3 shrink-0 flex items-center justify-between">
        <h1 className="text-xl font-semibold text-[#1A1814]">Profile Review</h1>
        <button onClick={handleLogout} className="p-2 rounded-full hover:bg-[#F0EDE9] transition-colors">
          <LogOut className="h-4 w-4 text-[#7A7672]" />
        </button>
      </div>

      <div className="flex-1 overflow-auto px-4 pb-4 space-y-4">
        {/* Cover photo */}
        {coverPhoto && (
          <div className="relative w-full aspect-[4/3] rounded-2xl overflow-hidden bg-[#F0EDE9]">
            <img src={coverPhoto.url} alt="Profile" className="w-full h-full object-cover no-screenshot" onContextMenu={e => e.preventDefault()} draggable={false} />
            <div className="absolute bottom-0 left-0 right-0 p-4" style={{ background: 'linear-gradient(to top, rgba(26,24,20,0.72) 0%, transparent 100%)' }}>
              <p className="text-white font-semibold text-lg leading-tight">
                {profile.display_name}{profile.last_initial ? ` ${profile.last_initial.charAt(0)}.` : ''}{age ? `, ${age}` : ''}
              </p>
              {profile.city && <p className="text-white/70 text-sm mt-0.5">{profile.city}</p>}
            </div>
          </div>
        )}

        {/* Status message */}
        <div className="px-4 py-3.5 rounded-2xl" style={{ background: 'linear-gradient(135deg, #F7D7EA 0%, #EDE0EA 100%)', border: '1px solid #E7C6DF' }}>
          <p className="text-sm font-semibold text-[#8A3976]">Your selfie is being reviewed</p>
          <p className="text-xs text-[#A8A5A0] mt-1 leading-relaxed">A Jiv team member will verify your selfie within 24 hours. Once approved, you can start connecting with the community.</p>
        </div>

        {/* Verification checklist */}
        <div className="bg-white rounded-2xl p-4 border border-[rgba(0,0,0,0.05)]">
          <p className="text-[10px] uppercase tracking-[0.10em] font-semibold text-[#A8A5A0] mb-3">Verification progress</p>
          <div className="space-y-3">
            <StatusRow icon={<Camera className="h-4 w-4" />} label="Selfie verified" done={profile.selfie_verified === true} pending={!profile.selfie_verified} />
            <StatusRow icon={<Users className="h-4 w-4" />} label={`Community vouches · ${vouchCount} received`} done={vouchCount >= 2} />
            <StatusRow icon={<Calendar className="h-4 w-4" />} label={`${months} month${months !== 1 ? 's' : ''} on Jiv`} done={months >= 1} />
            <StatusRow icon={<Flag className="h-4 w-4" />} label="No reports" done={(profile.report_count || 0) === 0} flagged={(profile.report_count || 0) > 0} />
          </div>
        </div>

        {/* Community tier explanations */}
        <div className="bg-white rounded-2xl p-4 border border-[rgba(0,0,0,0.05)]">
          <p className="text-[10px] uppercase tracking-[0.10em] font-semibold text-[#A8A5A0] mb-3">Community tiers</p>
          <div className="space-y-4">
            <TierRow
              dot="bg-[#7A7672]"
              label="Member"
              desc="Your selfie is approved. Community members in your city can vouch for you. You can request vouches from your profile once approved."
            />
            <TierRow
              dot="bg-[#8A3976]"
              label="Vouched"
              desc="Vouched by 2 or more community members you know. Full access to discovery, matches, and messaging."
            />
            <TierRow
              dot=""
              label="Jiv Verified"
              desc="Founding members and referral invitees. Can give vouches, share referral codes, and help grow the community."
              gradient
            />
          </div>
        </div>

        {/* Profile info */}
        {(profile.profession || profile.sect || profile.practice_level) && (
          <div className="bg-white rounded-2xl p-4 border border-[rgba(0,0,0,0.05)]">
            <p className="text-[10px] uppercase tracking-[0.10em] font-semibold text-[#A8A5A0] mb-3">Your profile</p>
            <div className="space-y-1.5 text-sm">
              {profile.profession && <p><span className="text-[#A8A5A0]">Profession · </span><span className="text-[#1A1814]">{profile.profession}</span></p>}
              {profile.sect && <p><span className="text-[#A8A5A0]">Sect · </span><span className="text-[#1A1814]">{profile.sect}</span></p>}
              {profile.practice_level && <p><span className="text-[#A8A5A0]">Practice · </span><span className="text-[#1A1814]">{profile.practice_level}</span></p>}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 pb-6 shrink-0 pt-3 bg-white border-t border-[rgba(0,0,0,0.04)]">
        <p className="text-center text-xs text-[#A8A5A0] mb-3">
          Questions? <a href="mailto:hello@jivapp.in" className="text-[#C857A8]">Contact us</a>
        </p>
        <Button onClick={handleLogout} variant="outline" className="w-full rounded-full h-11 border-[#C857A8] text-[#C857A8] hover:bg-[#C857A8]/10">
          Log out
        </Button>
      </div>
    </div>
  );
}

function getMonthsOnPlatform(createdAt) {
  if (!createdAt) return 0;
  const created = new Date(createdAt), now = new Date();
  return Math.max(0, (now.getFullYear() - created.getFullYear()) * 12 + (now.getMonth() - created.getMonth()));
}

function StatusRow({ icon, label, done, pending, flagged }) {
  return (
    <div className="flex items-center gap-3">
      <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${done ? 'bg-[#E9F9EF]' : flagged ? 'bg-[#FEE8E8]' : 'bg-[#F0EDE9]'}`}>
        <span className={done ? 'text-[#27AE60]' : flagged ? 'text-[#E74C3C]' : 'text-[#C8C3BE]'}>{icon}</span>
      </div>
      <p className="text-sm text-[#1A1814] flex-1 leading-tight">{label}</p>
      {done && <Check className="h-4 w-4 text-[#27AE60] shrink-0" />}
      {pending && <span className="text-xs text-[#E67E22] font-medium shrink-0">Pending</span>}
      {flagged && <span className="text-xs text-[#E74C3C] font-medium shrink-0">Flagged</span>}
    </div>
  );
}

function TierRow({ dot, label, desc, gradient }) {
  return (
    <div className="flex items-start gap-3">
      <div
        className={`w-2.5 h-2.5 rounded-full shrink-0 mt-1.5 ${!gradient ? dot : ''}`}
        style={gradient ? { background: 'linear-gradient(135deg, #C857A8, #6F2A62)' } : {}}
      />
      <div>
        <p className="text-sm font-semibold text-[#1A1814]">{label}</p>
        <p className="text-xs text-[#A8A5A0] mt-0.5 leading-relaxed">{desc}</p>
      </div>
    </div>
  );
}
